import { useEffect, useRef, useState, type CSSProperties } from 'react';

/*
 * The stars of the void, in front of its smoke (SpaceBackground): drawn once, and again only when
 * the window changes size, at the screen's own resolution, so each is a crisp round point however
 * large the screen. Most are faint pin-pricks; some are brighter, a few have a halo and faint
 * rays. They come in the colors of stars (blue-white, white, pale gold, a rare rose) and keep their
 * places (a fixed seed). A few of the brighter ones twinkle (CSS: an opacity animation the
 * compositor runs alone), unless motion is reduced.
 */

interface Star {
  readonly x: number;
  readonly y: number;
  /** Radius in CSS pixels. */
  readonly r: number;
  /** Brightness, 0 to 1. */
  readonly a: number;
  /** "r, g, b". */
  readonly rgb: string;
  readonly bright: boolean;
}

interface Twinkle {
  readonly x: number;
  readonly y: number;
  readonly rgb: string;
  readonly seconds: number;
  readonly delay: number;
}

const COLORS: readonly (readonly [string, number])[] = [
  ['214, 226, 255', 0.38],
  ['255, 255, 255', 0.3],
  ['190, 208, 255', 0.16],
  ['255, 233, 196', 0.11],
  ['255, 206, 232', 0.05],
];
/** One star per this many square CSS pixels, within bounds. */
const AREA_PER_STAR = 5200;
const MIN_STARS = 50;
const MAX_STARS = 480;
const TWINKLES = 12;

/** A small seeded random number generator (mulberry32): the same sky every time. */
function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The stars of a sky this size, in CSS pixels. */
export function makeStars(width: number, height: number): Star[] {
  const random = seeded(0x5ea7);
  const count = Math.round(
    Math.min(MAX_STARS, Math.max(MIN_STARS, (width * height) / AREA_PER_STAR)),
  );
  const color = (): string => {
    let roll = random();
    for (const [rgb, weight] of COLORS) {
      if (roll < weight) return rgb;
      roll -= weight;
    }
    return COLORS[0]![0];
  };
  const stars: Star[] = [];
  for (let i = 0; i < count; i++) {
    const kind = random();
    const bright = kind > 0.965;
    const small = kind > 0.78;
    stars.push({
      x: random() * width,
      y: random() * height,
      r: bright ? 1.05 + random() * 0.55 : small ? 0.6 + random() * 0.4 : 0.3 + random() * 0.32,
      a: bright ? 0.85 + random() * 0.15 : small ? 0.55 + random() * 0.35 : 0.3 + random() * 0.4,
      rgb: color(),
      bright,
    });
  }
  return stars;
}

/** A star pixel is this many CSS pixels wide: the stars are pixel art. */
const PX = 3;

/** A star in pixels: one, or for a bright one a cross of light with a dimmer tip on each arm. */
function drawStar(ctx: CanvasRenderingContext2D, s: Star): void {
  const x = Math.floor(s.x / PX);
  const y = Math.floor(s.y / PX);
  ctx.fillStyle = `rgba(${s.rgb}, ${s.a})`;
  ctx.fillRect(x, y, 1, 1);
  if (!s.bright) return;
  ctx.fillStyle = `rgba(${s.rgb}, ${s.a * 0.55})`;
  ctx.fillRect(x - 1, y, 1, 1);
  ctx.fillRect(x + 1, y, 1, 1);
  ctx.fillRect(x, y - 1, 1, 1);
  ctx.fillRect(x, y + 1, 1, 1);
  ctx.fillStyle = `rgba(${s.rgb}, ${s.a * 0.22})`;
  ctx.fillRect(x - 2, y, 1, 1);
  ctx.fillRect(x + 2, y, 1, 1);
  ctx.fillRect(x, y - 2, 1, 1);
  ctx.fillRect(x, y + 2, 1, 1);
}

export function StarField({ still }: { still: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [twinkles, setTwinkles] = useState<readonly Twinkle[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    let drawn = '';
    const draw = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      // Only a real change of size: the stars stay put while toolbars come and go.
      const size = `${width}x${height}`;
      if (size === drawn || width === 0 || height === 0) return;
      drawn = size;
      // One canvas pixel per star pixel, scaled up without smoothing (styles.css).
      canvas.width = Math.ceil(width / PX);
      canvas.height = Math.ceil(height / PX);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';
      const stars = makeStars(width, height);
      for (const s of stars) drawStar(ctx, s);
      const random = seeded(0x71);
      setTwinkles(
        stars
          .filter((s) => s.r > 0.6)
          .slice(0, TWINKLES)
          .map((s) => ({
            x: s.x,
            y: s.y,
            rgb: s.rgb,
            seconds: 2.6 + random() * 3.4,
            delay: -random() * 6,
          })),
      );
      setReady(true);
    };
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, []);

  return (
    <div className={`star-field ${ready ? 'is-ready' : ''}`} aria-hidden="true">
      <canvas ref={ref} className="star-canvas" />
      {!still &&
        twinkles.map((t, i) => (
          <span
            key={i}
            className="star-twinkle"
            style={
              {
                // On the star's own pixel, so the twinkle lands on it.
                left: Math.floor(t.x / PX) * PX,
                top: Math.floor(t.y / PX) * PX,
                '--star': t.rgb,
                animationDuration: `${t.seconds}s`,
                animationDelay: `${t.delay}s`,
              } as CSSProperties
            }
          />
        ))}
    </div>
  );
}
